import { useState } from 'react';
import { Building2, ChevronDown, Users } from 'lucide-react';
import {
  DEFAULT_SESSION_SHARING_POLICY,
  allowedShareAccessLevels,
  type ResourceGrantAccess,
  type ResourceGrantSubjectType,
  type SessionSharingPolicy,
  type User,
} from '@reflex/shared';
import { Button } from '@reflex/ui/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@reflex/ui/components/ui/command';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@reflex/ui/components/ui/dropdown-menu';
import { SectionLabel } from '@reflex/ui/components/layout/Typography';
import { UserAvatar } from '@reflex/ui/components/shared/UserAvatar';
import type { OwnerSelectTeamOption } from '@reflex/ui/components/ownership/OwnerSelect';

/** One subject a resource is (or will be) shared with, at an access level. */
export interface ShareSelection {
  subjectType: ResourceGrantSubjectType;
  subjectId: string;
  /** Display name of the subject ("Backend team", a member's name, the org name). */
  subjectLabel: string;
  access: ResourceGrantAccess;
  /**
   * Id of the grant this selection came from, when it is an existing share
   * rather than one the user just added. Carried through edits so the
   * caller can tell "already granted" from "to be granted" and reconcile
   * the two — see `ShareAgentDialog`. Absent on the launch dialog's
   * pending shares, where no grant exists yet.
   */
  grantId?: string;
  /**
   * Exclusive end of the existing grant's sharing interval (epoch ms), or
   * null/absent for one that never lapses. Carried so re-issuing a grant
   * at a different access level keeps the expiry it already had.
   */
  expiresAt?: number | null;
}

export interface SharePickerProps {
  value: ShareSelection[];
  onChange: (value: ShareSelection[]) => void;
  /** Teams offered as share targets. */
  teams: OwnerSelectTeamOption[];
  /**
   * Every member the caller can see. Used both to offer share targets and
   * to put a face and a handle on an existing share's row, so pass the
   * whole roster and narrow the *offers* with {@link excludeUserIds}.
   */
  users: User[];
  /**
   * Members to leave out of the search results — the signed-in user and
   * the resource's owner, who cannot meaningfully be shared with. They
   * stay in {@link users} so a row addressed to them still renders as a
   * person.
   */
  excludeUserIds?: readonly string[];
  /** The active organization id — the `subjectId` for an org-wide share. */
  orgId: string;
  /** Org display name for the general-access row. */
  orgName?: string | null;
  /**
   * Show the shares without any way to change them — the view someone who
   * can read the resource but not re-share it gets. Hides the search field
   * and swaps every access menu for plain text.
   */
  readOnly?: boolean;
  /**
   * The org's ceiling on what a share may confer. A level the org turned
   * off is offered greyed out rather than hidden, so a member reads "my
   * organization does not allow this" instead of wondering where the option
   * went. Defaults to unrestricted; the server enforces the real policy.
   */
  policy?: SessionSharingPolicy;
}

const ACCESS_LABELS: Record<ResourceGrantAccess, string> = {
  view: 'Can view',
  edit: 'Can edit',
};

/** The one line explaining a greyed-out level, or null when nothing is greyed. */
function policyHint(policy: SessionSharingPolicy): string | null {
  if (!policy.view && !policy.edit) return 'Your organization does not allow sharing sessions.';
  if (!policy.edit) return 'Your organization does not allow sharing with edit access.';
  if (!policy.view) return 'Your organization does not allow sharing with view access.';
  return null;
}

/**
 * Generic share editor, laid out like the Notion/Google Docs share dialog:
 * a search field to add people and teams, the current shares as one sorted
 * list with a per-row access menu, and a "General access" row at the bottom
 * controlling the org-wide share. Purely presentational — the caller owns
 * the selection list and decides when (and against which resource) shares
 * are actually issued.
 *
 * With {@link SharePickerProps.readOnly} it becomes the same list without
 * the levers: who has access, at what level, and nothing to change.
 */
export function SharePicker({
  value,
  onChange,
  teams,
  users,
  excludeUserIds,
  orgId,
  orgName,
  readOnly = false,
  policy = DEFAULT_SESSION_SHARING_POLICY,
}: SharePickerProps) {
  const [query, setQuery] = useState('');
  // A subject is added at the least access the org still allows. With view
  // sharing off and edit on, "view" is not a level this org can confer, so
  // adding someone at it would build a share the server refuses.
  const defaultAccess = allowedShareAccessLevels(policy)[0] ?? 'view';

  const orgShare = value.find((share) => share.subjectType === 'org') ?? null;
  const pickedShares = value
    .filter((share) => share.subjectType !== 'org')
    .sort((a, b) => a.subjectLabel.localeCompare(b.subjectLabel));
  const isPicked = (subjectType: ResourceGrantSubjectType, subjectId: string) =>
    value.some((share) => share.subjectType === subjectType && share.subjectId === subjectId);

  const add = (subjectType: ResourceGrantSubjectType, subjectId: string, subjectLabel: string) => {
    onChange([...value, { subjectType, subjectId, subjectLabel, access: defaultAccess }]);
    setQuery('');
  };
  const setAccess = (target: ShareSelection, access: ResourceGrantAccess) => {
    onChange(value.map((share) => (share === target ? { ...share, access } : share)));
  };
  const remove = (target: ShareSelection) => {
    onChange(value.filter((share) => share !== target));
  };
  const setOrgAccess = (access: ResourceGrantAccess | null) => {
    const rest = value.filter((share) => share.subjectType !== 'org');
    onChange(
      access === null
        ? rest
        : [
            ...rest,
            {
              subjectType: 'org',
              subjectId: orgId,
              subjectLabel: orgName ?? 'the organization',
              access,
            },
          ],
    );
  };

  const searching = !readOnly && query.trim().length > 0;
  const orgLabel = orgName ?? 'the organization';

  return (
    <div data-testid="share-picker">
      {readOnly ? null : (
        <Command className="h-auto">
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder="Add people and teams..."
          />
          {/* Results only appear while typing; an empty query shows the
            current shares instead, like the Notion dialog. */}
          {searching && (
            <CommandList>
              <CommandEmpty>No matches.</CommandEmpty>
              <CommandGroup>
                {teams
                  .filter((team) => !isPicked('team', team.ownerTeamId))
                  .map((team) => (
                    <CommandItem
                      key={team.ownerTeamId}
                      value={`team ${team.name}`}
                      data-testid={`share-picker-team-${team.ownerTeamId}`}
                      onSelect={() => add('team', team.ownerTeamId, `${team.name} team`)}
                    >
                      <SubjectGlyph type="team" />
                      <div className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate text-sm">{team.name}</span>
                        <span className="truncate text-xs text-muted-foreground">Team</span>
                      </div>
                    </CommandItem>
                  ))}
                {users
                  .filter(
                    (user) => !isPicked('user', user.id) && !excludeUserIds?.includes(user.id),
                  )
                  .map((user) => {
                    const display = user.name ?? user.login;
                    return (
                      <CommandItem
                        key={user.id}
                        value={`${user.login} ${user.name ?? ''} ${user.email}`}
                        data-testid={`share-picker-user-${user.id}`}
                        onSelect={() => add('user', user.id, display)}
                      >
                        <UserAvatar userName={display} avatarUrl={user.avatarUrl ?? undefined} />
                        <div className="flex min-w-0 flex-1 flex-col">
                          <span className="truncate text-sm">{display}</span>
                          <span className="truncate text-xs text-muted-foreground">
                            @{user.login}
                          </span>
                        </div>
                      </CommandItem>
                    );
                  })}
              </CommandGroup>
            </CommandList>
          )}
        </Command>
      )}

      {!searching && (
        <div className="flex flex-col gap-0.5 p-1.5">
          {pickedShares.map((share) => {
            const user =
              share.subjectType === 'user' ? users.find((u) => u.id === share.subjectId) : null;
            return (
              <ShareRow
                key={`${share.subjectType}:${share.subjectId}`}
                data-testid={`share-picker-row-${share.subjectId}`}
                icon={
                  share.subjectType === 'user' ? (
                    <UserAvatar
                      userName={share.subjectLabel}
                      avatarUrl={user?.avatarUrl ?? undefined}
                    />
                  ) : (
                    <SubjectGlyph type="team" />
                  )
                }
                label={share.subjectLabel}
                // A user row whose member record is not in `users` (someone
                // outside the caller's visible roster) still reads as a
                // person, not as a team.
                sublabel={
                  share.subjectType === 'user' ? (user ? `@${user.login}` : 'Member') : 'Team'
                }
                control={
                  readOnly ? (
                    <AccessText>{ACCESS_LABELS[share.access]}</AccessText>
                  ) : (
                    <AccessMenu
                      ariaLabel={`Change access for ${share.subjectLabel}`}
                      current={ACCESS_LABELS[share.access]}
                      accessValue={share.access}
                      policy={policy}
                      onAccessChange={(access) => setAccess(share, access)}
                      onRemove={() => remove(share)}
                    />
                  )
                }
              />
            );
          })}
          {readOnly && pickedShares.length === 0 && (
            <p className="px-1.5 py-1 text-sm text-muted-foreground">
              Not shared with anyone directly.
            </p>
          )}
          <SectionLabel as="span" className="block px-1.5 pt-2 pb-1">
            General access
          </SectionLabel>
          <ShareRow
            data-testid="share-picker-org-row"
            icon={<SubjectGlyph type="org" />}
            label={`Everyone in ${orgLabel}`}
            sublabel={
              orgShare ? 'Anyone in the organization has access' : 'Only people added above'
            }
            control={
              readOnly ? (
                <AccessText>{orgShare ? ACCESS_LABELS[orgShare.access] : 'No access'}</AccessText>
              ) : (
                <AccessMenu
                  ariaLabel="Change general access"
                  current={orgShare ? ACCESS_LABELS[orgShare.access] : 'No access'}
                  accessValue={orgShare?.access ?? null}
                  policy={policy}
                  onAccessChange={(access) => setOrgAccess(access)}
                  onNoAccess={() => setOrgAccess(null)}
                />
              )
            }
          />
        </div>
      )}
    </div>
  );
}

/** The read-only twin of {@link AccessMenu}: the level, no lever. */
function AccessText({ children }: { children: React.ReactNode }) {
  return <span className="shrink-0 px-3 text-xs text-muted-foreground">{children}</span>;
}

/** Round muted glyph for non-user share subjects (teams and the whole org). */
export function SubjectGlyph({ type }: { type: 'team' | 'org' }) {
  const Icon = type === 'team' ? Users : Building2;
  return (
    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted ring-1 ring-border">
      <Icon className="icon-sm text-muted-foreground" />
    </span>
  );
}

function ShareRow({
  icon,
  label,
  sublabel,
  control,
  'data-testid': dataTestId,
}: {
  icon: React.ReactNode;
  label: string;
  sublabel?: string;
  control: React.ReactNode;
  'data-testid'?: string;
}) {
  return (
    <div className="flex items-center gap-2 rounded-md px-1.5 py-1" data-testid={dataTestId}>
      {icon}
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm">{label}</span>
        {sublabel ? (
          <span className="truncate text-xs text-muted-foreground">{sublabel}</span>
        ) : null}
      </div>
      {control}
    </div>
  );
}

/**
 * The per-row access menu: "Can view" / "Can edit" (radio-checked on the
 * current level), plus "No access" for the general-access row or "Remove"
 * for an individual share.
 */
function AccessMenu({
  ariaLabel,
  current,
  accessValue,
  policy,
  onAccessChange,
  onRemove,
  onNoAccess,
}: {
  ariaLabel: string;
  current: string;
  accessValue: ResourceGrantAccess | null;
  policy: SessionSharingPolicy;
  onAccessChange: (access: ResourceGrantAccess) => void;
  onRemove?: () => void;
  onNoAccess?: () => void;
}) {
  const hint = policyHint(policy);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="shrink-0 gap-1 text-xs text-muted-foreground"
          aria-label={ariaLabel}
        >
          {current}
          <ChevronDown className="icon-xs" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuRadioGroup
          value={accessValue ?? 'none'}
          onValueChange={(next) => {
            // A disallowed level can only arrive here through a synthetic
            // event; drop it rather than build a share the server refuses.
            if (next === 'view' || next === 'edit') {
              if (policy[next]) onAccessChange(next);
            } else onNoAccess?.();
          }}
        >
          {/* A level the org disallows stays visible but inert: it is the
            answer to "why can't I do this", and Radix keeps a disabled
            radio item unselectable by pointer and keyboard alike. */}
          <DropdownMenuRadioItem value="view" disabled={!policy.view}>
            Can view
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="edit" disabled={!policy.edit}>
            Can edit
          </DropdownMenuRadioItem>
          {onNoAccess && <DropdownMenuRadioItem value="none">No access</DropdownMenuRadioItem>}
        </DropdownMenuRadioGroup>
        {hint && (
          <p className="px-2 py-1.5 text-2xs text-muted-foreground" data-testid="share-policy-hint">
            {hint}
          </p>
        )}
        {onRemove && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={onRemove}>Remove</DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
